import { getDocumentsPageUrl } from '@constants'
import { AI_HELP_RESERVED_SPACE_CSS_VAR } from '@constants/layout'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useLocalStorage, useOnboardSteps, useSessionStorage } from '@hooks'
import { BusinessOnboardingStepKey, IBusinessOnboardStep } from '@query'
import { useScreen } from '@utils'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { FaArrowsAlt, FaGripVertical } from 'react-icons/fa'
import styled, { css } from 'styled-components'
import { CompanionBody } from './CompanionBody'
import { CompanionHeader } from './CompanionHeader'
import { ProgressRing } from './ProgressRing'
import { getCompanionCreditTotals, getStepCredits, getStepCreditsEarned } from './companionCredits'
import {
  getGroupStatus,
  getHandledCount,
  isBackfilledOnboard,
  isLegacyOnboard
} from './companionStepState'
import { CompanionPosition, useCompanionDrag } from './useCompanionDrag'
import { getStartedSawUnfinishedKey, useBankHandover } from './useBankHandover'
import { useCelebration } from './useCelebration'

type StoredPosition = CompanionPosition | { x: number; y: number } | null

const PANEL_WIDTH = '25rem'
const FADE_TRANSITION = { duration: 0.16, ease: 'easeOut' as const }
const SNAP_TRANSITION = { duration: 0 }
const OPEN_MOTION = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 10 }
}

const toCompanionPosition = (value: StoredPosition): CompanionPosition | null => {
  if (!value || !('bottom' in value) || typeof value.bottom !== 'number') return null
  return { x: value.x, bottom: value.bottom }
}

export const GetStartedWidget: React.FC = () => {
  const { slug } = useBusinessContext()
  if (!slug) return null
  return <GetStartedWidgetForBusiness key={slug} slug={slug} />
}

const GetStartedWidgetForBusiness: React.FC<{ slug: string }> = ({ slug }) => {
  const [t] = useTranslation()
  const { data: business } = useBusinessContext()
  const isAdmin = usePermission(PermissionSet.ADMIN)
  const { hasFinished, hasSteps } = useOnboardSteps()
  const steps: IBusinessOnboardStep[] = business?.onboard_steps ?? []
  const reduceMotion = useReducedMotion()
  const bankBurstRef = React.useRef(false)
  const approveCreditsSeenRef = React.useRef<string | null | undefined>(undefined)

  const [isCollapsed, setIsCollapsed] = React.useState(false)
  const [storedPosition, setStoredPosition] = useLocalStorage<StoredPosition>(
    `getStartedWidget.${slug}.position`,
    null
  )
  const [hasDragged, setHasDragged] = useLocalStorage<boolean>(
    `getStartedWidget.${slug}.hasDragged`,
    false
  )
  const [dismissed, setDismissed] = useLocalStorage<boolean>(
    `getStartedWidget.${slug}.dismissed`,
    false
  )
  const [sawUnfinished, setSawUnfinished] = useSessionStorage<boolean>(
    getStartedSawUnfinishedKey(slug),
    false
  )
  const [burstGroupKey, setBurstGroupKey] = React.useState<BusinessOnboardingStepKey | null>(null)
  const [displayEarned, setDisplayEarned] = React.useState<number | null>(null)
  const payoutRef = React.useRef({ from: 0, to: 0 })

  const position = toCompanionPosition(storedPosition)
  const setPosition = (value: CompanionPosition | null) => setStoredPosition(value)

  const history = useHistory()
  const { isMobile } = useScreen()
  const { justReturned, returnKind, isHandoverPending, startBankHandover, acknowledgeReturn } =
    useBankHandover(slug)
  const drag = useCompanionDrag({
    position,
    setPosition,
    setHasDragged
  })

  if (business && hasSteps && !hasFinished && !sawUnfinished) {
    setSawUnfinished(true)
  }

  const bankStep = steps.find(step => step.group_key === BusinessOnboardingStepKey.BANK_INTEGRATION)
  const bankReturnCelebration =
    justReturned && bankStep !== undefined && getGroupStatus(bankStep) === 'done'

  if (bankReturnCelebration && isCollapsed) {
    setIsCollapsed(false)
  }

  const completedThisSession = Boolean(sawUnfinished || justReturned)
  const shouldCelebrate = Boolean(isAdmin && completedThisSession && hasFinished)
  const { containerRef } = useCelebration({ shouldCelebrate })

  const handled = getHandledCount(steps)
  const { total: creditTotal, earned } = getCompanionCreditTotals(steps)
  const allDone = hasFinished && steps.length > 0
  const legacyOnboard = isLegacyOnboard(steps)
  const missedSteps = isBackfilledOnboard(business?.created_at, steps)
  const shownEarned = displayEarned ?? earned
  const approveStep = steps.find(
    step => step.group_key === BusinessOnboardingStepKey.APPROVE_TRANSACTIONS
  )

  const startCreditBurst = (groupKey: BusinessOnboardingStepKey, from: number, to: number) => {
    payoutRef.current = { from, to }
    setBurstGroupKey(groupKey)
    setDisplayEarned(from)
    setIsCollapsed(false)
  }

  if (bankReturnCelebration && !bankBurstRef.current) {
    bankBurstRef.current = true
    if (bankStep && bankStep.credits_granted_at) {
      const payout = getStepCreditsEarned(bankStep)
      startCreditBurst(BusinessOnboardingStepKey.BANK_INTEGRATION, earned - payout, earned)
    }
  }

  if (business && approveCreditsSeenRef.current === undefined) {
    approveCreditsSeenRef.current = approveStep?.credits_granted_at ?? null
  } else if (approveCreditsSeenRef.current === null && approveStep?.credits_granted_at) {
    approveCreditsSeenRef.current = approveStep.credits_granted_at
    const payout = getStepCreditsEarned(approveStep)
    if (payout > 0) {
      startCreditBurst(BusinessOnboardingStepKey.APPROVE_TRANSACTIONS, earned - payout, earned)
    }
  }

  React.useEffect(() => {
    if (burstGroupKey === null) return

    const { from, to } = payoutRef.current
    const stepsCount = 14
    let tick = 0
    const interval = window.setInterval(() => {
      tick += 1
      setDisplayEarned(Math.round(from + (to - from) * (tick / stepsCount)))
      if (tick >= stepsCount) window.clearInterval(interval)
    }, 45)
    const timeout = window.setTimeout(() => {
      setBurstGroupKey(null)
      setDisplayEarned(null)
    }, 1500)

    return () => {
      window.clearInterval(interval)
      window.clearTimeout(timeout)
    }
  }, [burstGroupKey])

  const shouldShow = isAdmin && hasSteps && !dismissed && (completedThisSession || !hasFinished)

  if (!shouldShow) return null

  const collapsed = isCollapsed && !bankReturnCelebration
  const anchorRight = !position
  const fadeTransition = reduceMotion ? SNAP_TRANSITION : FADE_TRANSITION
  const panelTitle =
    bankReturnCelebration && !allDone
      ? t('dashboard.getStartedWidget.welcomeBack')
      : allDone
      ? t(
          legacyOnboard
            ? 'dashboard.getStartedWidget.setupComplete'
            : 'dashboard.getStartedWidget.allSet'
        )
      : t(
          missedSteps
            ? 'dashboard.getStartedWidget.titleMissed'
            : 'dashboard.getStartedWidget.title'
        )
  const pillLabel = isHandoverPending
    ? t('dashboard.getStartedWidget.stepInProgress', {
        current: Math.min(handled + 1, steps.length),
        total: steps.length
      })
    : allDone
    ? t('dashboard.getStartedWidget.setupComplete')
    : t('dashboard.getStartedWidget.finishSettingUp')
  const showDragHint = !hasDragged && !collapsed && !burstGroupKey
  const showIntroHelp = !bankReturnCelebration && !allDone && handled <= 1

  const onBankCta = () => {
    setIsCollapsed(true)
    startBankHandover()
  }

  const onGoToTransactions = () => {
    acknowledgeReturn()
    if (isMobile) setIsCollapsed(true)
    history.push(getDocumentsPageUrl(slug))
  }

  const onResolved = (groupKey: BusinessOnboardingStepKey, kind: 'done' | 'skipped') => {
    acknowledgeReturn()
    const step = steps.find(item => item.group_key === groupKey)
    const payout = kind === 'done' && step ? getStepCredits(step) : 0
    startCreditBurst(groupKey, earned, earned + payout)
  }

  const onCollapse = () => {
    acknowledgeReturn()
    setIsCollapsed(true)
  }

  return (
    <Layer>
      <Dock
        ref={node => {
          drag.wrapRef.current = node
          containerRef.current = node
        }}
        style={drag.dockStyle}
        $dragging={drag.isDragging}
        tabIndex={0}
        onKeyDown={event =>
          drag.onKeyDown(event, () => {
            if (!collapsed) onCollapse()
          })
        }
      >
        <AnimatePresence>
          {showDragHint && (
            <DragHint
              key="hint"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.32, ease: 'easeOut' }}
            >
              <FaArrowsAlt />
              {t('dashboard.getStartedWidget.dragHint')}
            </DragHint>
          )}
        </AnimatePresence>

        <Shell $anchorRight={anchorRight} $pulse={collapsed && isHandoverPending && !reduceMotion}>
          <AnimatePresence initial={false} mode="wait">
            {collapsed ? (
              <Pill
                key="pill"
                type="button"
                title={t('dashboard.getStartedWidget.dragToMove')}
                onPointerDown={drag.beginDrag}
                onClick={() => {
                  if (drag.consumeClickIfDragged()) return
                  setIsCollapsed(false)
                }}
                initial={reduceMotion ? false : OPEN_MOTION.initial}
                animate={OPEN_MOTION.animate}
                exit={reduceMotion ? undefined : OPEN_MOTION.exit}
                transition={fadeTransition}
              >
                <FaGripVertical />
                <ProgressRing handled={handled} total={steps.length} size="pill" />
                <span>{pillLabel}</span>
                {!isHandoverPending && (
                  <PillMeta>
                    {handled}/{steps.length}
                  </PillMeta>
                )}
              </Pill>
            ) : (
              <PanelLayer
                key="panel"
                initial={reduceMotion ? false : OPEN_MOTION.initial}
                animate={OPEN_MOTION.animate}
                exit={reduceMotion ? undefined : OPEN_MOTION.exit}
                transition={fadeTransition}
              >
                <CompanionHeader
                  title={panelTitle}
                  handled={handled}
                  total={steps.length}
                  earned={shownEarned}
                  creditTotal={creditTotal}
                  isCounting={burstGroupKey !== null}
                  hasDragged={hasDragged}
                  showSnapBack={drag.hasMoved}
                  showIntroHelp={showIntroHelp}
                  introTooltip={
                    missedSteps ? t('dashboard.getStartedWidget.introMissed') : undefined
                  }
                  isDragging={drag.isDragging}
                  onPointerDown={drag.beginDrag}
                  onSnapBack={drag.snapBack}
                  onCollapse={onCollapse}
                  onDismiss={
                    allDone
                      ? () => {
                          acknowledgeReturn()
                          setSawUnfinished(false)
                          setDismissed(true)
                        }
                      : undefined
                  }
                />
                <CompanionBody
                  steps={steps}
                  earned={earned}
                  showSuccessfulBankReturn={bankReturnCelebration}
                  bankReturnKind={returnKind}
                  onGoToTransactions={onGoToTransactions}
                  allDone={allDone}
                  burstGroupKey={burstGroupKey}
                  onBankCta={onBankCta}
                  onResolved={onResolved}
                  onCollapse={onCollapse}
                />
              </PanelLayer>
            )}
          </AnimatePresence>
        </Shell>
      </Dock>
    </Layer>
  )
}

const Layer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 40;
`

const Dock = styled.div<{ $dragging: boolean }>`
  position: fixed;
  right: calc(${({ theme }) => theme.spacing.xl}rem + var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px));
  bottom: ${({ theme }) => theme.spacing.xl}rem;
  max-width: calc(
    100vw - ${({ theme }) => theme.spacing.xl * 2}rem - var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px)
  );
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  pointer-events: auto;
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'auto')};
`

const DragHint = styled(motion.div)`
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => `${theme.spacing.xs}rem ${theme.spacing.sm}rem`};
  border-radius: 100px;
  background: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.menuBg};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(3, 30, 51, 0.14);

  svg {
    font-size: 0.7rem;
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Shell = styled.div<{ $anchorRight: boolean; $pulse: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: ${({ $anchorRight }) => ($anchorRight ? 'flex-end' : 'flex-start')};
  width: max-content;
  max-width: 100%;

  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: companionPillPulse 2.2s ease-in-out infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}

  @keyframes companionPillPulse {
    0%,
    100% {
      box-shadow: 0 12px 32px rgba(3, 30, 51, 0.18), 0 0 0 0 rgba(27, 152, 245, 0.5);
    }
    50% {
      box-shadow: 0 12px 32px rgba(3, 30, 51, 0.18), 0 0 0 0.625rem rgba(27, 152, 245, 0);
    }
  }
`

const PanelLayer = styled(motion.div)`
  flex-shrink: 0;
  width: ${PANEL_WIDTH};
  max-width: calc(100vw - ${({ theme }) => theme.spacing.xl * 2}rem);
  max-height: calc(100dvh - ${({ theme }) => theme.spacing.xl * 2}rem);
  overflow: auto;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  box-shadow: 0 24px 56px rgba(3, 30, 51, 0.18);
`

const Pill = styled(motion.button)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) =>
    `${theme.spacing.sm}rem ${theme.spacing.md}rem ${theme.spacing.sm}rem ${theme.spacing.sm}rem`};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 100px;
  background: ${({ theme }) => theme.colors.menuBg};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: grab;
  touch-action: none;
  box-shadow: 0 24px 56px rgba(3, 30, 51, 0.18);

  &:active {
    cursor: grabbing;
  }

  svg:first-child {
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    color: ${({ theme }) => theme.dashboard.onNavyAxis};
  }
`

const PillMeta = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
`
